import { useEffect, useState } from 'preact/hooks';
import { getConfig } from '../config';
import { getTestimonials, postTestimonial, type TestimonialItem } from '../api';
import { store } from '../store';
import { openContextualLogin, openContextualSignup } from '../returnTo';
import { t } from '../i18n';
import { themeTokens } from '../themeTokens';

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

const PAGE_SIZE = 10;
const radius = themeTokens.radius;

function normalizeTestimonials(rows: TestimonialItem[]): TestimonialItem[] {
  return rows
    .filter((r) => (r?.message ?? '').trim() !== '')
    .map((r) => ({
      id: r.id,
      name: (r.name ?? '').trim() || t('testimonials.customer', 'Customer'),
      message: (r.message ?? '').trim(),
    }));
}

const errorBlockStyle =
  `padding:24px 20px;text-align:center;border:1px dashed var(--sw-surface-border,var(--sw-border,#dee2e6));border-radius:${radius};background:var(--sw-bg-muted,#f8f9fa);color:var(--sw-muted,#6c757d);font-size:var(--sw-font-size,14px);line-height:var(--sw-line-height,1.5)`;

function TestimonialsErrorBlock() {
  return (
    <div class="shop-widget-testimonials-error" style={errorBlockStyle}>
      {t('testimonials.loadError', 'Unable to load testimonials. Please try again later.')}
    </div>
  );
}

function TestimonialCard({ item }: { item: TestimonialItem }) {
  return (
    <article class="sw-testimonial-card" style={`padding:12px 14px;border:1px solid var(--sw-surface-border,var(--sw-border,#dee2e6));border-radius:${radius};background:#fff`}>
      <p style="margin:0 0 8px;color:var(--sw-text,#212529);line-height:1.45">"{item.message}"</p>
      <p style="margin:0;font-size:var(--sw-font-size-sm,13px);color:var(--sw-muted,#6c757d)">
        <strong style="color:var(--sw-text,#212529)">{item.name || t('testimonials.customer', 'Customer')}</strong>
      </p>
    </article>
  );
}

export function TestimonialsShortWidget() {
  const [state, setState] = useState<LoadState>('idle');
  const [items, setItems] = useState<TestimonialItem[]>([]);

  useEffect(() => {
    let active = true;
    setState('loading');
    getTestimonials(getConfig())
      .then((res) => {
        if (!active) return;
        const rows = normalizeTestimonials((res.result ?? []) as TestimonialItem[]);
        setItems(rows.slice(0, 3));
        setState('ready');
      })
      .catch(() => {
        if (!active) return;
        setState('error');
      });
    return () => {
      active = false;
    };
  }, []);

  if (state === 'loading') return <div class="shop-widget-loading">{t('testimonials.loading', 'Loading testimonials...')}</div>;
  if (state === 'error') return <TestimonialsErrorBlock />;
  if (!items.length) return <div class="text-muted">{t('testimonials.empty', 'No testimonials yet.')}</div>;

  return (
    <section class="shop-widget-testimonials-short" style="display:grid;grid-template-columns:1fr;gap:10px">
      {items.map((item, idx) => <TestimonialCard key={`${item.id ?? idx}`} item={item} />)}
    </section>
  );
}

function Pagination({ page, totalPages, onPageChange }: { page: number; totalPages: number; onPageChange: (p: number) => void }) {
  if (totalPages <= 1) return null;
  const btnStyle = `padding:6px 12px;margin:0 4px;border:1px solid var(--sw-surface-border,var(--sw-border,#dee2e6));border-radius:${radius};background:#fff;cursor:pointer;font-size:var(--sw-font-size,14px)`;
  const disabledStyle = btnStyle + ';opacity:0.5;cursor:not-allowed';
  return (
    <div style="display:flex;align-items:center;justify-content:center;gap:4px;margin-top:16px;flex-wrap:wrap">
      <button
        type="button"
        class="sw-testimonials-pagination-btn"
        style={page <= 1 ? disabledStyle : btnStyle}
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
      >
        {t('pagination.previous', 'Previous')}
      </button>
      <span style="font-size:var(--sw-font-size,14px);color:var(--sw-muted,#6c757d)">
        {page} / {totalPages}
      </span>
      <button
        type="button"
        class="sw-testimonials-pagination-btn"
        style={page >= totalPages ? disabledStyle : btnStyle}
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
      >
        {t('pagination.next', 'Next')}
      </button>
    </div>
  );
}

export function TestimonialsPageWidget() {
  const [state, setState] = useState<LoadState>('idle');
  const [items, setItems] = useState<TestimonialItem[]>([]);
  const [page, setPage] = useState(1);
  const [auth, setAuth] = useState(store.getAuth());
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [submitError, setSubmitError] = useState('');
  const [validationErrors, setValidationErrors] = useState<{ name?: string; message?: string }>({});

  useEffect(() => {
    return store.subscribe(() => setAuth(store.getAuth()));
  }, []);

  useEffect(() => {
    if (auth?.login) {
      const authName = (auth.name ?? auth.login ?? '').trim();
      if (authName) setName((prev) => (prev.trim() ? prev : authName));
    }
  }, [auth]);

  useEffect(() => {
    let active = true;
    setState('loading');
    getTestimonials(getConfig())
      .then((res) => {
        if (!active) return;
        setItems(normalizeTestimonials((res.result ?? []) as TestimonialItem[]));
        setState('ready');
      })
      .catch(() => {
        if (!active) return;
        setState('error');
      });
    return () => {
      active = false;
    };
  }, []);

  const isLoggedIn = !!auth?.login;
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = items.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  if (state === 'loading') return <div class="shop-widget-loading">{t('testimonials.loading', 'Loading testimonials...')}</div>;
  if (state === 'error') return <TestimonialsErrorBlock />;
  const listContent = !items.length
    ? <div class="text-muted">{t('testimonials.empty', 'No testimonials yet.')}</div>
    : (
      <>
        <section class="shop-widget-testimonials-page" style="display:grid;grid-template-columns:1fr;gap:12px">
          {pageItems.map((item, idx) => <TestimonialCard key={`${item.id ?? idx}`} item={item} />)}
        </section>
        <Pagination page={safePage} totalPages={totalPages} onPageChange={setPage} />
      </>
    );

  const canSubmit = name.trim().length > 1 && message.trim().length > 5 && submitState !== 'submitting';
  const onSubmit = async (e: Event) => {
    e.preventDefault();
    const nameValid = name.trim().length > 1;
    const messageValid = message.trim().length > 5;
    if (!nameValid || !messageValid) {
      setValidationErrors({
        name: nameValid ? undefined : t('testimonials.validationName', 'Name must be at least 2 characters.'),
        message: messageValid ? undefined : t('testimonials.validationMessage', 'Review must be at least 6 characters.'),
      });
      return;
    }
    setValidationErrors({});
    setSubmitState('submitting');
    setSubmitError('');
    const res = await postTestimonial(getConfig(), { name: name.trim(), message: message.trim() });
    if (res.status === 200) {
      setSubmitState('success');
      setName('');
      setMessage('');
      return;
    }
    setSubmitState('error');
    setSubmitError(res.error || t('testimonials.submitError', 'Unable to submit testimonial.'));
  };

  const formPanelStyle = `padding:14px;border:1px solid var(--sw-surface-border,var(--sw-border,#dee2e6));border-radius:${radius};background:#fff`;
  const fieldStyle = (hasError: boolean, marginBottom: number) =>
    `display:block;width:100%;padding:9px 11px;border:1px solid ${hasError ? '#dc2626' : 'var(--sw-surface-border,var(--sw-border,#dee2e6))'};border-radius:${radius};margin-bottom:${marginBottom}px;box-sizing:border-box`;

  const formSection = isLoggedIn ? (
    <section class="sw-testimonials-form-panel" style={formPanelStyle}>
      <h3 style="margin:0 0 10px;font-size:var(--sw-font-size,1rem)">{t('testimonials.formTitle', 'Leave a testimonial')}</h3>
      <form onSubmit={onSubmit}>
        <input
          class="sw-testimonials-field"
          value={name}
          onInput={(e) => { setName((e.target as HTMLInputElement).value); setValidationErrors((v) => ({ ...v, name: undefined })); }}
          placeholder={t('testimonials.namePlaceholder', 'Your name')}
          style={`${fieldStyle(!!validationErrors.name, validationErrors.name ? 4 : 8)}`}
        />
        {validationErrors.name && <p style="margin:0 0 8px;font-size:var(--sw-font-size-sm,12px);color:#dc2626">{validationErrors.name}</p>}
        <textarea
          class="sw-testimonials-field"
          value={message}
          onInput={(e) => { setMessage((e.target as HTMLTextAreaElement).value); setValidationErrors((v) => ({ ...v, message: undefined })); }}
          placeholder={t('testimonials.messagePlaceholder', 'Your review')}
          rows={4}
          style={`${fieldStyle(!!validationErrors.message, validationErrors.message ? 4 : 10)};resize:vertical`}
        />
        {validationErrors.message && <p style="margin:0 0 10px;font-size:var(--sw-font-size-sm,12px);color:#dc2626">{validationErrors.message}</p>}
        <button type="submit" class="btn btn-primary" disabled={!canSubmit}>
          {submitState === 'submitting' ? t('testimonials.submitting', 'Submitting...') : t('testimonials.submit', 'Submit review')}
        </button>
        {submitState === 'success' && (
          <p style="margin:10px 0 0;font-size:var(--sw-font-size-sm,13px);color:#16a34a">{t('testimonials.success', 'Thank you for your review! It will appear after moderation.')}</p>
        )}
        {submitState === 'error' && (
          <p style="margin:10px 0 0;font-size:var(--sw-font-size-sm,13px);color:#dc2626">{submitError}</p>
        )}
      </form>
    </section>
  ) : (
    <section class="sw-testimonials-form-panel" style={formPanelStyle}>
      <h3 style="margin:0 0 10px;font-size:var(--sw-font-size,1rem)">{t('testimonials.formTitle', 'Leave a testimonial')}</h3>
      <p class="text-muted mb-3">
        {t('testimonials.loginRequired', 'Please log in to leave a testimonial.')}
      </p>
      <a href="#" class="btn btn-primary me-2 mb-2" onClick={(e) => { e.preventDefault(); openContextualLogin(); }}>
        {t('nav.login', 'Sign In')}
      </a>
      <a href="#" class="btn btn-outline-primary mb-2" onClick={(e) => { e.preventDefault(); openContextualSignup(); }}>
        {t('nav.signup', 'Create Account')}
      </a>
    </section>
  );

  return (
    <div style="display:grid;grid-template-columns:1fr;gap:16px">
      {formSection}
      {listContent}
    </div>
  );
}
